import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useViewAsClient } from "@/lib/viewAsClient";
import { useAuth } from "@/lib/auth";
import { authHeaders } from "@/lib/api/_shared";

const POLL_MS = 60_000;
const ICON = {
  error:   <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />,
  warning: <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />,
  info:    <Info className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />,
};

function useEmailAlerts() {
  const { viewAsClientId: viewAs } = useViewAsClient() || {};
  const { assignments = [], hasRole } = useAuth();
  const clientId = hasRole(["super_admin"]) ? viewAs : assignments.find((a) => a.client_id && a.role === "client_admin")?.client_id || "";
  const [data, setData] = useState({ count: 0, alerts: [] });

  const load = useCallback(async () => {
    try {
      const url = new URL("/api/admin/email-alerts", process.env.REACT_APP_BACKEND_URL);
      if (clientId) url.searchParams.set("client_id", clientId);
      const res = await fetch(url.toString(), { headers: await authHeaders() });
      if (res.ok) setData(await res.json());
    } catch {
      // keep previous state
    }
  }, [clientId]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);
  return { ...data, multiTenant: !clientId };
}

const AlertItem = ({ alert: a, showTenant, onOpen }) => (
  <button type="button" onClick={() => onOpen(a.href)} data-testid={`email-alert-${a.key}`}
          className="w-full text-left flex gap-2 rounded-md p-2 hover:bg-slate-50 transition-colors">
    {ICON[a.severity]}
    <span className="min-w-0">
      <span className="block text-sm font-medium text-slate-900">
        {showTenant && <span className="text-slate-500">{a.client_name}: </span>}{a.title}
      </span>
      <span className="block text-xs text-slate-500">{a.detail}</span>
    </span>
  </button>
);

// Header alarm bell: tenants that can't send booking emails + failed / not-sent emails.
export default function EmailAlertsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { count, alerts, multiTenant } = useEmailAlerts();
  const openHref = (href) => { setOpen(false); navigate(href); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="email-alerts-bell" title="Email alerts"
                className="relative inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100 transition-colors">
          <Bell className={`h-4 w-4 ${count ? "text-red-600" : "text-slate-500"}`} />
          {count > 0 && (
            <span data-testid="email-alerts-count"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-[18px] text-white text-center">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-2" data-testid="email-alerts-panel">
        <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Email alerts</div>
        {alerts.length === 0 ? (
          <p className="px-2 py-3 text-sm text-slate-500" data-testid="email-alerts-empty">All good — booking emails are sending.</p>
        ) : (
          alerts.map((a) => <AlertItem key={a.key} alert={a} showTenant={multiTenant} onOpen={openHref} />)
        )}
      </PopoverContent>
    </Popover>
  );
}
